import { css } from '@emotion/react'
import { Formik } from 'formik'
import React from 'react'
import Toggle from 'react-toggle'

import ActionButton from '@/components/misc/ActionButton'
import UserAvatar from '@/components/misc/UserAvatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { CompanyUserCollectionItem } from '@/types/responses/company-users'

interface Props {
  companyUsers: CompanyUserCollectionItem[]
  selectedCompanyUserIds: number[]
  onChange: (selectedIds: number[]) => void
  isMutating: boolean
  onHide: () => void
  toCompanyPresentationName: string
}

// @todo: I think this will currently only show a max of 30 users.
// Can probably use the <SelectCompanyIndividuals /> component to handle more
// than 30 users.
const ConfigureEmailsFormModal: React.FC<Props> = ({
  companyUsers,
  selectedCompanyUserIds,
  onChange,
  isMutating,
  onHide,
  toCompanyPresentationName,
}) => {
  function handleSubmit(values: { selectedCompanyUserIds: number[] }) {
    onChange(values.selectedCompanyUserIds)
  }

  return (
    <Formik
      initialValues={{ selectedCompanyUserIds }}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, handleSubmit }) => {
        const toggleUserId = (userId: number) => {
          const currentIds = values.selectedCompanyUserIds
          const isCurrentlySelected = currentIds.includes(userId)

          if (isCurrentlySelected) {
            setFieldValue(
              'selectedCompanyUserIds',
              currentIds.filter((id) => id !== userId),
            )
          } else {
            setFieldValue('selectedCompanyUserIds', [...currentIds, userId])
          }
        }

        return (
          <Dialog open onHide={onHide}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Configure emails</DialogTitle>
              </DialogHeader>

              <DialogBody>
                <p className="mb-4">
                  Select any users you want to BCC on notifications of new
                  referrals sent to {toCompanyPresentationName} via this
                  calendar link.
                </p>

                <ul className="mb-4 flex list-none flex-col gap-1 pl-0">
                  {companyUsers.map((companyUser) => {
                    const isChecked = values.selectedCompanyUserIds.includes(
                      companyUser.id,
                    )

                    const inputId = `configure-referral-link-emails-user-${companyUser.id}`

                    return (
                      <li
                        key={companyUser.id}
                        className={cn(
                          'flex items-center rounded-lg hover:bg-blue-100',
                          isChecked ? 'bg-blue-100' : undefined,
                        )}
                        css={css`
                          transition: background-color 0.3s ease;
                        `}
                      >
                        <Label
                          htmlFor={inputId}
                          className="mb-2 grow cursor-pointer p-4"
                        >
                          <div className="flex flex-1 items-center gap-4 p-0">
                            <UserAvatar user={companyUser.user} />

                            <div className="flex flex-col gap-1">
                              <strong>{companyUser.user.fullName}</strong>

                              <span style={{ lineHeight: 1 }}>
                                {companyUser.user.email}
                              </span>
                            </div>
                          </div>
                        </Label>

                        <Toggle
                          id={inputId}
                          checked={isChecked}
                          onChange={() => toggleUserId(companyUser.id)}
                          disabled={isMutating}
                          className="absolute right-0 mr-6"
                        />
                      </li>
                    )
                  })}
                </ul>
              </DialogBody>

              <DialogFooter>
                <Button onClick={onHide} variant="outline-secondary">
                  Cancel
                </Button>

                <ActionButton
                  isProcessing={isMutating}
                  onClick={() => handleSubmit()}
                >
                  Save
                </ActionButton>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default ConfigureEmailsFormModal
